import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Badge, EmptyState } from '../components/ui.js';
import { Icon } from '../components/Icon.js';
import { formatMoney, type Currency } from '@chatrunner/schemas';

interface Balance {
  subscription_status: string;
  base_included: number;
  base_used: number;
  base_remaining: number;
  topup_remaining: number;
  total_remaining: number;
  current_period_start: string | null;
  current_period_end: string | null;
  low_threshold: number;
  is_low: boolean;
  recent_purchases: Array<{
    purchase_id: string;
    pack_name: string;
    credits: number;
    price_cents: number;
    currency: string;
    status: 'pending' | 'paid' | 'failed' | 'refunded';
    paid_at: string | null;
    created_at: string;
  }>;
}

interface Pack {
  pack_id: string; name: string; emoji: string;
  credits: number; price_cents: number; currency: string;
  display_order: number; highlight: 'popular' | 'best_value' | ''; tagline: string;
}

const STATUS_KIND: Record<Balance['recent_purchases'][number]['status'], 'ok' | 'warn' | 'danger' | 'muted'> = {
  paid: 'ok', pending: 'warn', failed: 'danger', refunded: 'muted',
};

export function Credits() {
  const api = useApi();
  const qc = useQueryClient();
  const { t, lang } = useLang();
  const dateLocale = lang === 'ar' ? 'ar-EG' : 'en-US';

  const { data: balance } = useQuery({
    queryKey: ['credits-balance'],
    queryFn: () => api.get<Balance>('/credits'),
    refetchInterval: 30_000,
  });
  const { data: packsData } = useQuery({
    queryKey: ['credit-packs'],
    queryFn: () => api.get<{ packs: Pack[] }>('/credits/packs'),
  });

  const [confirming, setConfirming] = useState<string | null>(null);

  const buy = useMutation({
    mutationFn: (pack_id: string) =>
      api.post<{ purchase_id: string; pack_name: string; credits: number }>(
        '/credits/purchase', { pack_id, payment_provider: 'manual_admin' },
      ),
    onSuccess: (data) => setConfirming(data.purchase_id),
  });

  const confirmMutation = useMutation({
    mutationFn: (purchase_id: string) => api.post(`/credits/purchase/${purchase_id}/confirm`, {}),
    onSuccess: () => { setConfirming(null); qc.invalidateQueries({ queryKey: ['credits-balance'] }); },
  });

  const included = balance?.base_included ?? 0;
  const used = balance?.base_used ?? 0;
  const remainingPct = included > 0 ? Math.max(0, Math.min(100, ((included - used) / included) * 100)) : 0;

  return (
    <div className="wrap section-gap">
      <PageHead title={t('page.credits.title')} sub={t('page.credits.sub')} />

      <div className="card card--pad">
        <div className="between" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>{t('page.credits.remaining')}</div>
            <div className="mono" style={{ fontSize: 40, fontWeight: 800, lineHeight: 1, color: 'var(--ink)' }}>
              {(balance?.total_remaining ?? 0).toLocaleString('en-US')}
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>{t('page.credits.remainingDetail')}</div>
            {balance?.is_low && (
              <div className="flex aic gap8" style={{ marginTop: 12 }}>
                <Badge kind="warn" plain icon="bell">{t('page.credits.low')}</Badge>
              </div>
            )}
          </div>
          <div style={{ textAlign: 'end' }}>
            <Badge kind={balance?.subscription_status === 'active' ? 'ok' : 'muted'} plain icon="checkcircle">
              {t('page.credits.subStatus')} {balance?.subscription_status === 'active' ? t('status.active') : balance?.subscription_status ?? '—'}
            </Badge>
            {balance?.current_period_end && (
              <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                {t('page.credits.renewal')} {new Date(balance.current_period_end).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' })}
              </div>
            )}
          </div>
        </div>

        <div className="grid2" style={{ marginTop: 22 }}>
          <div className="card card--pad" style={{ background: 'var(--bg-3)' }}>
            <div className="flex aic gap8" style={{ marginBottom: 10 }}>
              <Icon name="refresh" size={16} style={{ color: 'var(--accent)' }} />
              <div style={{ fontSize: 12.5, color: 'var(--ink-2)', fontWeight: 600 }}>{t('page.credits.monthly')}</div>
            </div>
            <div className="mono" style={{ fontSize: 22, fontWeight: 800 }}>
              {(balance?.base_remaining ?? 0).toLocaleString('en-US')}
              <span className="muted" style={{ fontSize: 12, fontWeight: 400, marginInlineStart: 6 }}>
                {t('page.credits.monthlyOf')} {included.toLocaleString('en-US')}
              </span>
            </div>
            <div style={{ height: 6, background: 'var(--bg-4)', borderRadius: 999, overflow: 'hidden', marginTop: 12 }}>
              <div style={{ height: '100%', width: `${remainingPct}%`, background: 'var(--accent)', transition: 'width .25s' }} />
            </div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>{t('page.credits.monthlyResets')}</div>
          </div>
          <div className="card card--pad" style={{ background: 'rgba(74, 217, 145, 0.08)', borderColor: 'rgba(74, 217, 145, 0.25)' }}>
            <div className="flex aic gap8" style={{ marginBottom: 10 }}>
              <Icon name="bolt" size={16} style={{ color: 'var(--ok)' }} />
              <div style={{ fontSize: 12.5, color: 'var(--ok)', fontWeight: 600 }}>{t('page.credits.topup')}</div>
            </div>
            <div className="mono" style={{ fontSize: 22, fontWeight: 800, color: 'var(--ok)' }}>
              {(balance?.topup_remaining ?? 0).toLocaleString('en-US')}
            </div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 16 }}>{t('page.credits.topupHint')}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="between" style={{ marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>{t('page.credits.buy.h')}</h2>
          <span className="muted" style={{ fontSize: 12 }}>{t('page.credits.buy.hint')}</span>
        </div>
        <div className="pgrid">
          {(packsData?.packs ?? []).map((p) => (
            <PackCard
              key={p.pack_id}
              pack={p}
              onBuy={() => buy.mutate(p.pack_id)}
              loading={buy.isPending && buy.variables === p.pack_id}
            />
          ))}
        </div>
      </div>

      <div>
        <h2 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 14px' }}>{t('page.credits.history')}</h2>
        {(balance?.recent_purchases ?? []).length === 0 ? (
          <EmptyState icon="card" h={t('page.credits.history.empty.h')} p={t('page.credits.history.empty.p')} />
        ) : (
          <div className="card tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('page.credits.col.pack')}</th>
                  <th>{t('page.credits.col.credits')}</th>
                  <th>{t('page.credits.col.price')}</th>
                  <th>{t('page.credits.col.status')}</th>
                  <th>{t('page.credits.col.date')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(balance?.recent_purchases ?? []).map((p) => (
                  <tr key={p.purchase_id}>
                    <td className="tbl__primary">{p.pack_name}</td>
                    <td className="mono">{p.credits.toLocaleString('en-US')}</td>
                    <td className="mono tbl__cell-strong">{formatMoney(p.price_cents, p.currency as Currency)}</td>
                    <td><Badge kind={STATUS_KIND[p.status]} plain>{t(`page.credits.purchase.${p.status}`)}</Badge></td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {new Date(p.created_at).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' })}
                    </td>
                    <td>
                      {p.status === 'pending' && (
                        <button className="btn btn--sm btn--primary" onClick={() => setConfirming(p.purchase_id)}>
                          <Icon name="check" />{t('page.credits.markPaid')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirming && (
        <ConfirmModal
          purchase={balance?.recent_purchases.find((p) => p.purchase_id === confirming)}
          onClose={() => setConfirming(null)}
          onConfirm={() => confirmMutation.mutate(confirming)}
          loading={confirmMutation.isPending}
        />
      )}
    </div>
  );
}

function PackCard({ pack, onBuy, loading }: { pack: Pack; onBuy: () => void; loading: boolean }) {
  const { t } = useLang();
  const isPopular = pack.highlight === 'popular';
  const isBest = pack.highlight === 'best_value';
  const pricePerCredit = (pack.price_cents / 100) / pack.credits;
  return (
    <div
      className="card card--pad"
      style={{
        position: 'relative',
        borderColor: isPopular ? 'var(--accent-line)' : isBest ? 'var(--warn-soft)' : undefined,
        boxShadow: isPopular ? '0 12px 40px -16px var(--accent-glow)' : undefined,
      }}
    >
      {(isPopular || isBest) && (
        <span style={{
          position: 'absolute', top: -10, insetInlineEnd: 14,
          background: isPopular ? 'var(--accent)' : 'var(--warn)',
          color: '#0c0822', padding: '4px 10px', borderRadius: 999,
          fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase',
        }}>
          {isPopular ? t('page.credits.pack.popular') : t('page.credits.pack.best')}
        </span>
      )}
      <div style={{
        width: 40, height: 40, borderRadius: 11, background: 'var(--accent-soft)',
        color: 'var(--accent)', display: 'grid', placeItems: 'center', marginBottom: 12,
      }}><Icon name="bolt" size={20} /></div>
      <div style={{ fontWeight: 800, fontSize: 16 }}>{pack.name}</div>
      <div className="mono" style={{ fontSize: 26, fontWeight: 800, marginTop: 6 }}>
        {pack.credits.toLocaleString('en-US')}
        <span className="muted" style={{ fontSize: 12, fontWeight: 400, marginInlineStart: 6 }}>{t('page.credits.pack.unit')}</span>
      </div>
      <div className="mono" style={{ fontSize: 14, color: 'var(--ink-2)', marginTop: 4 }}>
        {formatMoney(pack.price_cents, pack.currency as Currency)}
      </div>
      <div className="muted mono" style={{ fontSize: 11, marginTop: 2 }}>
        ${pricePerCredit.toFixed(3)} {t('page.credits.pack.perCredit')}
      </div>
      {pack.tagline && <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>{pack.tagline}</div>}
      <button
        className={`btn ${isPopular ? 'btn--primary' : 'btn--ghost'}`}
        onClick={onBuy}
        disabled={loading}
        style={{ width: '100%', marginTop: 16, justifyContent: 'center' }}
      >
        {loading ? t('page.credits.pack.starting') : t('page.credits.pack.buy')}
      </button>
    </div>
  );
}

function ConfirmModal({ purchase, onClose, onConfirm, loading }: {
  purchase?: Balance['recent_purchases'][number];
  onClose: () => void; onConfirm: () => void; loading: boolean;
}) {
  const { t } = useLang();
  return (
    <div className="modal__scrim" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{t('page.credits.modal.title')}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{t('page.credits.modal.sub')}</div>
          </div>
          <button className="modal__x" onClick={onClose} style={{ fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <div className="modal__body">
          {purchase ? (
            <div className="card card--pad" style={{ background: 'var(--bg-3)' }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{purchase.pack_name}</div>
              <div className="flex aic gap12 wrapf">
                <Badge kind="accent" plain icon="bolt">{purchase.credits.toLocaleString('en-US')}</Badge>
                <Badge kind="muted" plain icon="money">{formatMoney(purchase.price_cents, purchase.currency as Currency)}</Badge>
              </div>
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 13 }}>{t('page.credits.modal.empty')}</div>
          )}
        </div>
        <div className="modal__foot">
          <button className="btn btn--primary" onClick={onConfirm} disabled={loading}>
            <Icon name="check" />{loading ? t('page.credits.modal.confirming') : t('page.credits.markPaid')}
          </button>
          <button className="btn btn--ghost" onClick={onClose}>{t('common.cancel')}</button>
        </div>
      </div>
    </div>
  );
}
